import {
  BN,
  Marinade,
  MarinadeConfig,
  type MarinadeState as SDKMarinadeState,
  MarinadeUtils,
} from "@marinade.finance/marinade-ts-sdk";
import { type Connection, PublicKey, LAMPORTS_PER_SOL } from "@solana/web3.js";

import type { MarinadeState } from "solana/domain/marinade-state";
import { bnToNumber } from "utils/bn-to-number";

export const getMarinade = (
  cxn: Connection,
  publicKey?: PublicKey,
  referralCode?: PublicKey
) => {
  const config = new MarinadeConfig({
    connection: cxn,
    publicKey,
    referralCode,
  });
  return new Marinade(config);
};

export const getTvlStaking = async (
  cxn: Connection,
  marinadeState?: SDKMarinadeState
) => {
  const { state } =
    marinadeState ?? (await getMarinade(cxn).getMarinadeState());
  return MarinadeUtils.lamportsToSol(
    state.validatorSystem.totalActiveBalance
      .add(state.availableReserveBalance)
      .add(state.emergencyCoolingDown)
  );
};

export const getSolLegBalance = async (
  cxn: Connection,
  marinadeState?: SDKMarinadeState
) => {
  const { state, solLeg } =
    marinadeState ?? (await getMarinade(cxn).getMarinadeState());

  const solLegKey = await solLeg();
  return new BN(await cxn.getBalance(solLegKey)).sub(
    state.rentExemptForTokenAcc
  );
};

export const getMSolLegBalance = async (
  cxn: Connection,
  marinadeState?: SDKMarinadeState
) => {
  const { state } =
    marinadeState ?? (await getMarinade(cxn).getMarinadeState());

  const tokenBalance = await cxn.getTokenAccountBalance(state.liqPool.msolLeg);

  return new BN(tokenBalance.value.amount);
};

export const getLiquidityPool = async (
  cxn: Connection,
  marinadeState?: SDKMarinadeState
) => {
  const state = marinadeState ?? (await getMarinade(cxn).getMarinadeState());

  const [mSolLegBalance, solLegBalance] = await Promise.all([
    getMSolLegBalance(cxn, state),
    getSolLegBalance(cxn, state),
  ]);

  const totalLiqPoolValueLamports = solLegBalance.add(
    mSolLegBalance.muln(state.mSolPrice)
  );

  const tvl = MarinadeUtils.lamportsToSol(totalLiqPoolValueLamports);
  const supply = MarinadeUtils.lamportsToSol(state.state.liqPool.lpSupply);
  const lpPrice = tvl / supply;

  return {
    solBalance: MarinadeUtils.lamportsToSol(solLegBalance),
    mSolBalance: MarinadeUtils.lamportsToSol(mSolLegBalance),
    tvl,
    lpPrice,
    supply,
    lpMint: state.state.liqPool.lpMint,
    lpTarget: MarinadeUtils.lamportsToSol(
      state.state.liqPool.lpLiquidityTarget
    ),
    cap: MarinadeUtils.lamportsToSol(state.state.liqPool.liquiditySolCap),
  };
};

export const getMarinadeState = async (
  cxn: Connection
): Promise<MarinadeState> => {
  const marinadeState = await getMarinade(cxn).getMarinadeState();

  const [liquidityPool, tvlStaking] = await Promise.all([
    getLiquidityPool(cxn, marinadeState),
    getTvlStaking(cxn, marinadeState),
  ]);

  return {
    sdkMarinadeState: marinadeState,
    reserveAddress: await marinadeState.reserveAddress(),
    minStake: marinadeState.state.stakeSystem.minStake,
    tvlStaking,
    totalTvl: tvlStaking + liquidityPool.tvl,
    mSolPrice: marinadeState.mSolPrice,
    liquidityPool,
    rentExemptForTokenAcc: marinadeState.state.rentExemptForTokenAcc.toNumber(),
    activeStake: MarinadeUtils.lamportsToSol(
      marinadeState.state.validatorSystem.totalActiveBalance
    ),
    stakeDelta: bnToNumber(marinadeState.stakeDelta()) / LAMPORTS_PER_SOL,
    reserveBalance: MarinadeUtils.lamportsToSol(
      marinadeState.state.availableReserveBalance
    ),
    treasuryCut: marinadeState.state.rewardFee.basisPoints,
  };
};

export const getMarinadeReferralPartnerState = async (
  cxn: Connection,
  referralCode: PublicKey
) => {
  try {
    const marinade = getMarinade(cxn, PublicKey.default, referralCode);
    const referralState = await marinade.getReferralPartnerState();

    return referralState.state;
  } catch (err) {
    return undefined;
  }
};

export const getDelayedUnstakeTickets = async (
  cxn: Connection,
  beneficiary: PublicKey
) => {
  return getMarinade(cxn).getDelayedUnstakeTickets(beneficiary);
};

export const getEstimatedUnstakeTicketDueDate = async (cxn: Connection) => {
  return getMarinade(cxn).getEstimatedUnstakeTicketDueDate();
};

export const getUsersVoteRecord = async (
  cxn: Connection,
  userAddress: PublicKey
) => {
  return getMarinade(cxn, userAddress).getUsersVoteRecord(userAddress);
};

export const getEpochInfo = async (cxn: Connection) => {
  return MarinadeUtils.getEpochInfo(cxn);
};
